import type { AccountingTypeModel } from '@/features/master-table/models';
import type { PaymentType } from '@/features/project/projects/_models/payment';
import {
  type ContextType,
  type Dispatch,
  type FC,
  type PropsWithChildren,
  type SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import type { z } from 'zod';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';

type PreviewRowState =
  | {
      type: 'expenditure-cart';
      accountingType: z.infer<typeof AccountingTypeModel> | undefined;
    }
  | {
      type: 'expenditure-item';
      expenditureCartId: string;
    }
  | {
      type: 'payment-group';
    }
  | {
      type: 'payee-payment';
      paymentGroupId: string;
    }
  | {
      type: 'contractor-payment';
    }
  | {
      type: 'payment-contract';
      paymentGroupId: string;
      paymentId: string;
      paymentType: PaymentType;
    }
  | null;

const Context = createContext<
  | {
      previewRowState: PreviewRowState;
      setPreviewRowState: Dispatch<SetStateAction<PreviewRowState>>;
    }
  | undefined
>(undefined);

const _useContext = (): NonNullable<ContextType<typeof Context>> => {
  const context = useContext(Context);
  if (!context) {
    throw new Error(
      'usePreviewRowState must be used within PreviewRowStateProvider.',
    );
  }
  return context;
};

export const usePreviewRowState = () => {
  return _useContext();
};

export const PreviewRowStateProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const [previewRowState, setPreviewRowState] = useState<PreviewRowState>(null);
  const { drawerState } = useProjectDetailDrawerState();

  useEffect(() => {
    if (!drawerState.open) {
      setPreviewRowState(null);
    }
  }, [drawerState]);

  return (
    <Context.Provider value={{ previewRowState, setPreviewRowState }}>
      {children}
    </Context.Provider>
  );
};
